"use client";

import { useEffect, useState } from "react";

import BookingLink from "@/components/shared/booking-link/BookingLink";
import { cn } from "@/lib/utils/cn";
import { tourPageStickyCtaThemeStyles as themeStyles } from "./themeStyles";
import type { TourPageThemeKey } from "./types";

interface TourPageStickyCtaProps {
  bookingUrl: string;
  theme: TourPageThemeKey;
  tourName: string;
  meta: string;
  ariaLabel: string;
}

export default function TourPageStickyCta({
  bookingUrl,
  theme,
  tourName,
  meta,
  ariaLabel,
}: TourPageStickyCtaProps) {
  const [isVisible, setIsVisible] = useState(false);
  const styles = themeStyles[theme];

  useEffect(() => {
    const heroCta = document.querySelector("[data-tour-hero-cta]");
    if (!heroCta) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(!entry.isIntersecting);
      },
      { threshold: 0 },
    );

    observer.observe(heroCta);

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        setIsVisible(false);
      }
    };

    const handleFocusOut = () => {
      const rect = heroCta.getBoundingClientRect();
      setIsVisible(rect.bottom < 0);
    };

    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);

    return () => {
      observer.disconnect();
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
    };
  }, []);

  return (
    <aside
      aria-label={`${tourName} booking`}
      aria-hidden={!isVisible}
      data-tour-page-sticky-cta
      className={cn(
        "fixed inset-x-0 bottom-0 z-[1000] flex items-center justify-between gap-3 border-t px-4 py-2 text-white shadow-[0_-4px_16px_rgba(0,0,0,0.26)] backdrop-blur transition-transform duration-200 ease-out motion-reduce:transition-none lg:hidden",
        styles.stickyBg,
        styles.stickyBorder,
        isVisible ? "translate-y-0" : "pointer-events-none translate-y-full",
      )}
      style={{ paddingBottom: "calc(0.5rem + env(safe-area-inset-bottom))" }}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-[15px] font-bold leading-tight">{tourName}</p>
        <p className={cn("truncate text-[13px] leading-tight", styles.meta)}>
          {meta}
        </p>
      </div>
      <BookingLink
        href={bookingUrl}
        aria-label={ariaLabel}
        tabIndex={isVisible ? undefined : -1}
        className={cn(
          "inline-flex min-h-12 shrink-0 items-center justify-center rounded-md px-5 text-[15px] font-bold text-white shadow-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 motion-reduce:transition-none",
          styles.button,
        )}
      >
        Book Now
      </BookingLink>
    </aside>
  );
}
